import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Megaphone, Plus, Edit3, Trash2, Send, X, Mail, RotateCcw, Download } from 'lucide-react';
import { clubsService } from '../../../services';
import './ManageClubs.css';
import VenueSubnav from './VenueSubnav';
import VenueAccessBanner from './VenueAccessBanner';

const emptyForm = {
    title: '',
    subject: '',
    content: '',
    audience: 'AllMembers',
    deliveryChannel: 'Email',
    membershipTypeFilter: '',
    joinedAfterDate: '',
    sendByEmail: true,
    emailOnlyOptedIn: true
};

const audiencePresets = [
    {
        key: 'all_members',
        label: 'All Members',
        description: 'Default full member audience',
        values: { audience: 'AllMembers', membershipTypeFilter: '', emailOnlyOptedIn: true }
    },
    {
        key: 'admins',
        label: 'Admins',
        description: 'Owners + admins only',
        values: { audience: 'Admins', membershipTypeFilter: '', emailOnlyOptedIn: true }
    },
    {
        key: 'owners',
        label: 'Owners',
        description: 'Owners only',
        values: { audience: 'Owners', membershipTypeFilter: '', emailOnlyOptedIn: true }
    },
    {
        key: 'members_only',
        label: 'Members Only',
        description: 'Member level users only',
        values: { audience: 'MembersOnly', membershipTypeFilter: '', emailOnlyOptedIn: true }
    },
    {
        key: 'all_opted_in_off',
        label: 'All (Urgent)',
        description: 'Includes members regardless of email opt-in',
        values: { audience: 'AllMembers', membershipTypeFilter: '', emailOnlyOptedIn: false }
    }
];

const ManageCommunications = () => {
    const { clubId } = useParams();
    const navigate = useNavigate();
    const [club, setClub] = useState(null);
    const [communications, setCommunications] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showDeliveryModal, setShowDeliveryModal] = useState(false);
    const [editingCommunication, setEditingCommunication] = useState(null);
    const [selectedCommunication, setSelectedCommunication] = useState(null);
    const [deliveries, setDeliveries] = useState([]);
    const [form, setForm] = useState(emptyForm);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoadingDeliveries, setIsLoadingDeliveries] = useState(false);
    const [activeCommunicationActionId, setActiveCommunicationActionId] = useState(null);
    const [selectedPresetKey, setSelectedPresetKey] = useState('all_members');
    const [historySearch, setHistorySearch] = useState('');
    const [historyStatus, setHistoryStatus] = useState('All');

    const load = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const [clubData, communicationData] = await Promise.all([
                clubsService.getClub(clubId),
                clubsService.getClubCommunications(clubId)
            ]);
            setClub(clubData);
            setCommunications(communicationData);
        } catch (err) {
            setError(err.message || 'Failed to load venue communications.');
        } finally {
            setIsLoading(false);
        }
    }, [clubId]);

    useEffect(() => {
        load();
    }, [load]);

    const isPremiumVenue = Boolean(club?.isPremiumVenue || (club?.profileTier || '').toLowerCase() === 'premium');
    const openUpgrade = () => navigate(`/dashboard/club/${clubId}/plan`);

    const orderedCommunications = useMemo(
        () => [...communications].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
        [communications]
    );

    const communicationStats = useMemo(() => {
        const sent = communications.filter((item) => item.status === 'Sent').length;
        const draft = communications.filter((item) => item.status !== 'Sent').length;
        const withFailures = communications.filter((item) => Number(item.failedCount || 0) > 0).length;
        return { sent, draft, withFailures };
    }, [communications]);

    const historyItems = useMemo(() => {
        return orderedCommunications.filter((item) => {
            const matchesStatus = historyStatus === 'All' ? true : item.status === historyStatus;
            const query = historySearch.trim().toLowerCase();
            const matchesSearch = !query
                ? true
                : (item.title || '').toLowerCase().includes(query)
                    || (item.subject || '').toLowerCase().includes(query)
                    || (item.audience || '').toLowerCase().includes(query);
            return matchesStatus && matchesSearch;
        });
    }, [orderedCommunications, historySearch, historyStatus]);

    const applyPreset = (preset) => {
        setSelectedPresetKey(preset.key);
        setForm((prev) => ({
            ...prev,
            audience: preset.values.audience,
            membershipTypeFilter: preset.values.membershipTypeFilter,
            emailOnlyOptedIn: preset.values.emailOnlyOptedIn
        }));
    };

    const openCreate = () => {
        if (!isPremiumVenue) {
            openUpgrade();
            return;
        }

        setEditingCommunication(null);
        setForm(emptyForm);
        setSelectedPresetKey('all_members');
        setShowModal(true);
    };

    const openEdit = (communication) => {
        if (!isPremiumVenue) {
            openUpgrade();
            return;
        }

        setEditingCommunication(communication);
        setForm({
            title: communication.title || '',
            subject: communication.subject || '',
            content: communication.content || '',
            audience: communication.audience || 'AllMembers',
            deliveryChannel: communication.deliveryChannel || 'Email',
            membershipTypeFilter: communication.membershipTypeFilter || '',
            joinedAfterDate: communication.joinedAfterDate?.slice(0, 10) || '',
            sendByEmail: communication.sendByEmail ?? true,
            emailOnlyOptedIn: communication.emailOnlyOptedIn ?? true
        });
        setSelectedPresetKey('');
        setShowModal(true);
    };

    const openDeliveries = async (communication) => {
        setSelectedCommunication(communication);
        setShowDeliveryModal(true);
        setIsLoadingDeliveries(true);
        setError(null);
        try {
            const data = await clubsService.getClubCommunicationDeliveries(clubId, communication.clubCommunicationId);
            setDeliveries(data || []);
        } catch (err) {
            setError(err.message || 'Failed to load delivery details.');
            setDeliveries([]);
        } finally {
            setIsLoadingDeliveries(false);
        }
    };

    const saveCommunication = async (sendNow = false) => {
        if (!isPremiumVenue) {
            openUpgrade();
            return;
        }

        setIsSubmitting(true);
        setError(null);
        try {
            const payload = {
                ...form,
                joinedAfterDate: form.joinedAfterDate || null
            };

            if (editingCommunication) {
                await clubsService.updateClubCommunication(clubId, editingCommunication.clubCommunicationId, payload);
            } else {
                await clubsService.createClubCommunication(clubId, { ...payload, sendNow });
            }

            if (editingCommunication && sendNow) {
                await clubsService.sendClubCommunication(clubId, editingCommunication.clubCommunicationId);
            }

            setShowModal(false);
            await load();
        } catch (err) {
            setError(err.message || 'Failed to save communication.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSend = async (communication) => {
        if (!isPremiumVenue) {
            openUpgrade();
            return;
        }

        if (!window.confirm(`Send "${communication.title}" now?`)) {
            return;
        }

        try {
            await clubsService.sendClubCommunication(clubId, communication.clubCommunicationId);
            await load();
        } catch (err) {
            setError(err.message || 'Failed to send communication.');
        }
    };

    const handleDelete = async (communication) => {
        if (!isPremiumVenue) {
            openUpgrade();
            return;
        }

        if (!window.confirm(`Delete "${communication.title}"?`)) {
            return;
        }

        try {
            await clubsService.deleteClubCommunication(clubId, communication.clubCommunicationId);
            await load();
        } catch (err) {
            setError(err.message || 'Failed to delete communication.');
        }
    };

    const handleRetryFailed = async (communication) => {
        if (!isPremiumVenue) {
            openUpgrade();
            return;
        }

        if (!window.confirm(`Retry failed recipients for "${communication.title}"?`)) {
            return;
        }

        setActiveCommunicationActionId(communication.clubCommunicationId);
        setError(null);

        try {
            const result = await clubsService.retryFailedClubCommunicationDeliveries(clubId, communication.clubCommunicationId);
            await load();
            alert(`${result.deliveredNow || 0} recipients delivered on retry. ${result.failedToProcess || 0} still failed.`);
        } catch (err) {
            setError(err.message || 'Failed to retry failed recipients.');
        } finally {
            setActiveCommunicationActionId(null);
        }
    };

    const handleResendFailedOnly = async (communication) => {
        if (!isPremiumVenue) {
            openUpgrade();
            return;
        }

        if (!window.confirm(`Resend this communication to failed recipients only for "${communication.title}"?`)) {
            return;
        }

        setActiveCommunicationActionId(communication.clubCommunicationId);
        setError(null);

        try {
            const result = await clubsService.resendFailedClubCommunicationDeliveries(clubId, communication.clubCommunicationId);
            await load();
            alert(`${result.deliveredNow || 0} recipients delivered. ${result.failedToProcess || 0} remain failed.`);
        } catch (err) {
            setError(err.message || 'Failed to resend to failed recipients.');
        } finally {
            setActiveCommunicationActionId(null);
        }
    };

    const handleExportDeliveries = async (communication) => {
        if (!isPremiumVenue) {
            openUpgrade();
            return;
        }

        setActiveCommunicationActionId(communication.clubCommunicationId);
        setError(null);

        try {
            const csvContent = await clubsService.exportClubCommunicationDeliveries(clubId, communication.clubCommunicationId);
            const blob = new Blob([csvContent || ''], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `communication-${communication.clubCommunicationId}-deliveries.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setError(err.message || 'Failed to export delivery logs.');
        } finally {
            setActiveCommunicationActionId(null);
        }
    };

    if (isLoading) {
        return (
            <div className="manage-clubs-page">
                <div className="manage-clubs-loading">
                    <div className="manage-clubs-spinner"></div>
                    <p>Loading communications...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="manage-clubs-page">
            <button className="club-dashboard-back" onClick={() => navigate(`/dashboard/club/${clubId}/dashboard`)}>
                <ChevronLeft size={18} />
                Back to {club?.name || 'Venue'} Venue Dashboard
            </button>

            <VenueSubnav clubId={clubId} />

            <VenueAccessBanner club={club} clubId={clubId} navigate={navigate} premiumOnlyFeature featureLabel="Communications" />

            <div className="manage-section-header">
                <div className="manage-section-header-content">
                    <div className="manage-section-header-icon icon-blue">
                        <Megaphone size={24} />
                    </div>
                    <div>
                        <h1>Targeted Communications</h1>
                        <p>Create segmented venue updates for members, admins, or owners at {club?.name}</p>
                    </div>
                </div>
                <button className="manage-section-add-btn" onClick={openCreate} disabled={!isPremiumVenue}>
                    <Plus size={18} />
                    {isPremiumVenue ? 'New Message' : 'Upgrade For Communications'}
                </button>
            </div>

            {!isPremiumVenue ? (
                <div className="venue-premium-lock-note">
                    Communications is a Premium venue feature. Free venues can review message history here, but creating and sending venue communications is locked until upgrade.
                </div>
            ) : null}

            <div className="communication-history-summary">
                <div className="communication-history-pill">
                    <strong>{communicationStats.sent}</strong>
                    <span>Sent</span>
                </div>
                <div className="communication-history-pill">
                    <strong>{communicationStats.draft}</strong>
                    <span>Draft / Queued</span>
                </div>
                <div className="communication-history-pill warning">
                    <strong>{communicationStats.withFailures}</strong>
                    <span>With failures</span>
                </div>
            </div>

            {error && <div className="manage-clubs-error">{error}</div>}

            {orderedCommunications.length === 0 ? (
                <div className="manage-clubs-empty">
                    <div className="manage-clubs-empty-icon">
                        <Megaphone size={48} />
                    </div>
                    <h3>No Messages Yet</h3>
                    <p>Create your first targeted venue update and choose who should receive it.</p>
                </div>
            ) : (
                <div className="members-list">
                    {orderedCommunications.map((communication) => (
                        <div key={communication.clubCommunicationId} className="member-card">
                            <div className="member-avatar">
                                <Mail size={18} />
                            </div>
                            <div className="member-info">
                                <h4>{communication.title}</h4>
                                <p>{communication.subject || communication.content}</p>
                                <span className="member-username">
                                    {communication.audience} - {communication.status} - {communication.deliveredCount}/{communication.recipientCount} delivered - {communication.failedCount || 0} failed
                                </span>
                                {communication.membershipTypeFilter && (
                                    <span className="member-username">
                                        Membership filter: {communication.membershipTypeFilter}
                                    </span>
                                )}
                            </div>
                            <div className="member-actions" style={{ display: 'flex', gap: '8px' }}>
                                {communication.status === 'Sent' && (
                                    <>
                                        <button className="member-action-btn" onClick={() => openDeliveries(communication)} title="View delivery details">
                                            <Mail size={18} />
                                        </button>
                                        <button
                                            className="member-action-btn"
                                            onClick={() => handleRetryFailed(communication)}
                                            disabled={activeCommunicationActionId === communication.clubCommunicationId}
                                            title="Retry failed recipients"
                                        >
                                            <RotateCcw size={18} />
                                        </button>
                                        <button
                                            className="member-action-btn"
                                            onClick={() => handleResendFailedOnly(communication)}
                                            disabled={activeCommunicationActionId === communication.clubCommunicationId}
                                            title="Resend to failed only"
                                        >
                                            <Send size={18} />
                                        </button>
                                        <button
                                            className="member-action-btn"
                                            onClick={() => handleExportDeliveries(communication)}
                                            disabled={activeCommunicationActionId === communication.clubCommunicationId}
                                            title="Export delivery CSV"
                                        >
                                            <Download size={18} />
                                        </button>
                                    </>
                                )}
                                {communication.status !== 'Sent' && (
                                    <>
                                        <button className="member-action-btn" onClick={() => openEdit(communication)} disabled={!isPremiumVenue}>
                                            <Edit3 size={18} />
                                        </button>
                                        <button className="member-action-btn" onClick={() => handleSend(communication)} disabled={!isPremiumVenue}>
                                            <Send size={18} />
                                        </button>
                                    </>
                                )}
                                <button className="member-action-btn" onClick={() => handleDelete(communication)} disabled={!isPremiumVenue}>
                                    <Trash2 size={18} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <div className="communication-history-panel">
                <div className="communication-history-header">
                    <h3>Communication History</h3>
                    <div className="communication-history-controls">
                        <input
                            placeholder="Search title, subject, audience..."
                            value={historySearch}
                            onChange={(e) => setHistorySearch(e.target.value)}
                        />
                        <select value={historyStatus} onChange={(e) => setHistoryStatus(e.target.value)}>
                            <option value="All">All statuses</option>
                            <option value="Sent">Sent</option>
                            <option value="Draft">Draft</option>
                            <option value="Queued">Queued</option>
                        </select>
                    </div>
                </div>
                {historyItems.length === 0 ? (
                    <p className="communication-history-empty">No communications match these filters.</p>
                ) : (
                    <div className="communication-history-list">
                        {historyItems.slice(0, 12).map((item) => (
                            <div key={item.clubCommunicationId} className="communication-history-item">
                                <strong>{item.title}</strong>
                                <span>{item.audience} • {item.status}</span>
                                <span>
                                    {item.sentAt
                                        ? `Sent ${new Date(item.sentAt).toLocaleString()}`
                                        : `Created ${new Date(item.createdAt).toLocaleString()}`}
                                </span>
                                <span>{item.deliveredCount || 0}/{item.recipientCount || 0} delivered • {item.failedCount || 0} failed</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {showModal && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{editingCommunication ? 'Edit Communication' : 'New Communication'}</h2>
                            <button className="modal-close" onClick={() => setShowModal(false)}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="form-group">
                                <label>Title</label>
                                <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                            </div>
                            <div className="form-group">
                                <label>Email Subject</label>
                                <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
                            </div>
                            <div className="form-group">
                                <label>Message</label>
                                <textarea rows={6} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} />
                            </div>
                            <div className="form-group">
                                <label>Saved Audience</label>
                                <div className="saved-audience-grid">
                                    {audiencePresets.map((preset) => (
                                        <button
                                            type="button"
                                            key={preset.key}
                                            className={`saved-audience-btn ${selectedPresetKey === preset.key ? 'active' : ''}`}
                                            onClick={() => applyPreset(preset)}
                                        >
                                            <strong>{preset.label}</strong>
                                            <span>{preset.description}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Audience</label>
                                <select value={form.audience} onChange={(e) => {
                                    setSelectedPresetKey('');
                                    setForm({ ...form, audience: e.target.value });
                                }}>
                                    <option value="AllMembers">All Members</option>
                                    <option value="MembersOnly">Members Only</option>
                                    <option value="Admins">Admins</option>
                                    <option value="Owners">Owners</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Membership Type Filter (optional)</label>
                                <input
                                    placeholder="e.g. Annual, Guest, Full"
                                    value={form.membershipTypeFilter}
                                    onChange={(e) => {
                                        setSelectedPresetKey('');
                                        setForm({ ...form, membershipTypeFilter: e.target.value });
                                    }}
                                />
                            </div>
                            <div className="form-group">
                                <label>Joined After (optional)</label>
                                <input
                                    type="date"
                                    value={form.joinedAfterDate}
                                    onChange={(e) => setForm({ ...form, joinedAfterDate: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <label>Delivery Channel</label>
                                <select value={form.deliveryChannel} onChange={(e) => setForm({ ...form, deliveryChannel: e.target.value })}>
                                    <option value="Email">Email</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <input type="checkbox" checked={form.sendByEmail} onChange={(e) => setForm({ ...form, sendByEmail: e.target.checked })} />
                                    Send by email
                                </label>
                            </div>
                            <div className="form-group">
                                <label style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <input
                                        type="checkbox"
                                        checked={form.emailOnlyOptedIn}
                                        onChange={(e) => {
                                            setSelectedPresetKey('');
                                            setForm({ ...form, emailOnlyOptedIn: e.target.checked });
                                        }}
                                    />
                                    Only send to members who have opted into email notifications
                                </label>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                            <button className="btn-secondary" onClick={() => saveCommunication(false)} disabled={!form.title || !form.content || isSubmitting}>
                                Save Draft
                            </button>
                            <button className="btn-primary" onClick={() => saveCommunication(true)} disabled={!form.title || !form.content || isSubmitting}>
                                {isSubmitting ? 'Processing...' : editingCommunication ? 'Save and Send' : 'Create and Send'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showDeliveryModal && (
                <div className="modal-overlay" onClick={() => setShowDeliveryModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Delivery Details</h2>
                            <button className="modal-close" onClick={() => setShowDeliveryModal(false)}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className="modal-body">
                            <p>{selectedCommunication?.title}</p>
                            {isLoadingDeliveries ? (
                                <p>Loading delivery details...</p>
                            ) : deliveries.length === 0 ? (
                                <p>No delivery records found for this message.</p>
                            ) : (
                                <div className="members-list">
                                    {deliveries.map((delivery) => (
                                        <div key={delivery.clubCommunicationDeliveryId} className="member-card">
                                            <div className="member-avatar">
                                                <Mail size={18} />
                                            </div>
                                            <div className="member-info">
                                                <h4>{delivery.userName || `User ${delivery.userId}`}</h4>
                                                <p>{delivery.email}</p>
                                                <span className="member-username">
                                                    {delivery.status}{delivery.deliveredAt ? ` - ${new Date(delivery.deliveredAt).toLocaleString()}` : ''}
                                                </span>
                                                {delivery.errorMessage && (
                                                    <span className="member-username">Error: {delivery.errorMessage}</span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManageCommunications;
